import React, { useState, useEffect, useRef } from "react";
import {
  Modal, Button, Form, Row, Col, Card, InputGroup, Tab, Tabs, Pagination,
} from "react-bootstrap";

import {
  useCustomerRewardPoints,
  useCustomerSearch,
  useLocationSearch,
} from "modules/transactions/new-sales-entry/services/use-sales-entry.service";
import { toast } from "react-toastify";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { CountryOption, CustomerData, CustomerModalProps, NewCustomerForm } from "types/types";
import { getCustomerCreditAmountById } from "modules/repair-work/repair-work-search/services";
import { useSalesEntry } from "contexts/SalesEntryContext";
import { getCustomerById } from "modules/customer/services";
import { HttpStatus, SelectListItem } from "modules/core";
import notistack from "modules/core/notistack";
import { size } from "lodash";
import { useSelector } from "react-redux";
import { RootState } from "slices/app/store/store";
import ShippingAddressModal from "./AddressForm";
import { useLocation } from "react-router-dom";
import { getSavedCartDetailsByCartId } from "../../services/api.services";
import { getAllWalkInSourceForSelect } from "modules/master/walk-in-source/services";
import { calculateRewardDetails } from "utils/calculateRewardDetails";
import { apiClient } from "helpers/api_helper";

const getInitialFormState = (
  countries: CountryOption[] = [],
  states: CountryOption[] = [],
  cities: CountryOption[] = [],
  user?: any
): NewCustomerForm => {
  // First try to find the user's store country, state, city
  // const storeCountry = countries.find(c => c.value === user?.store?.countryId?.toString());
  // const storeState = states.find(s => s.value === user?.store?.stateId?.toString());
  // const storeCity = cities.find(c => c.value === user?.store?.cityId?.toString());

  // If no store location, fall back to India
  // const defaultCountry = storeCountry ||
  //   countries.find(c => c.text.toLowerCase().includes("india")) ||
  //   null;

  const prefix = "+91";

  return {
    prefix,
    mobileNo: "",
    firstName: "",
    lastName: "",
    gender: "",
    email: "",
    address: "",
    country: null,
    state: null,
    city: null,
    zipCode: "",
    anniversary: "",
    birthday: "",
    engagement: "",
    spouseName: "",
    spouseContactNo: "",
    spouseBirthday: "",
    altMobileNo: "",
    gstNo: "",
    panCardNo: "",
    walkInSourceId: '',
    customerAddressList: []
  };
};

export const CustomerModal: React.FC<CustomerModalProps> = ({
  show,
  onHide,
  currentCustomer,
  onCustomerSelect,
}) => {
  const location = useLocation();
  const { cid, actions, state: addedItems } = useSalesEntry();
  const user = useSelector((state: RootState) => state?.Login?.user);
  const [activeTab, setActiveTab] = useState("customer");
  const [selectedShippingId, setSelectedShippingId] = useState<number | null>();
  const [selectedShipping, setShippingId] = useState<number | null>();



  useEffect(() => {
    if (cid || location.state?.customerId) {
      getCustomerData();
    }
  }, [cid, location.state?.customerId]);

  const decodeValue = (encoded: string | undefined): string | undefined => {
    try {
      if (!encoded) return undefined;
      return decodeURIComponent(atob(encoded));
    } catch (error) {
      console.error("Decoding failed for:", encoded, error);
      return undefined;
    }
  };

  const decodedId = decodeValue(cid);
  const calledRef = useRef(false);
  const rewardDataResultRef = useRef<any>(null);

  const getCustomerData = async () => {
    try {
      if (decodedId) {
        const response = await getCustomerById(decodedId);
        if (response.status === HttpStatus.Success) {
          handleEditCustomer(response.data);
          setShowCustomersList(true);
        }
      } else if (location.state?.customerId && location.state.cartId) {
        let parsedCartDetail = null;
        const res: any = await getSavedCartDetailsByCartId(location.state.cartId)
        if (res?.data?.cartDetailJson) {
          parsedCartDetail = JSON.parse(res.data.cartDetailJson);
        }
        if (parsedCartDetail?.BillingAddressId) {
          setSelectedShippingId(parsedCartDetail?.BillingAddressId)
        }
        if (parsedCartDetail?.ShippingAddressId) {
          setShippingId(parsedCartDetail?.ShippingAddressId)
        }
        let verifiedProducts: any[] = [];
        let matchedItems: any[] = [];
        const rewardRes = await apiClient.get('/api/v1/Reward/GetAllActiveRedemptionSettings');
        if (rewardRes?.data) {
          actions.setMaterialTypes(rewardRes.data);
        }
        if (Array.isArray(res?.data?.products) && res.data.products.length > 0) {
          verifiedProducts = res.data.products;
          const verifiedItemIds = new Set(
            verifiedProducts.map((p: any) => Number(p.id))
          );
          const savedItems = parsedCartDetail?.items ?? [];
          matchedItems = savedItems.filter((item: any) =>
            verifiedItemIds.has(Number(item.id))
          );
        }
        const result = calculateRewardDetails(matchedItems, {
          materialTypes: rewardRes?.data || []
        });
        const response = await getCustomerById(location.state?.customerId);
        if (response.status === HttpStatus.Success) {
          handleEditCustomer(response.data, parsedCartDetail?.BillingAddressId, parsedCartDetail?.ShippingAddressId);
          setShowCustomersList(true);
          window.history.replaceState({}, document.title);

          if (result?.distribution) {
            if (calledRef.current) return;
            calledRef.current = true
            rewardDataResultRef.current = result;
          }
        }
      }
    } catch (error: any) {
      notistack.error(error.message);
    }
  };

  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [newCustomerForm, setNewCustomerForm] = useState<NewCustomerForm>(getInitialFormState());
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [isCheckingMobile, setIsCheckingMobile] = useState(false);
  const [lastCheckedMobile, setLastCheckedMobile] = useState("");
  const [mobileExistsInDB, setMobileExistsInDB] = useState(false);
  const [showCustomersList, setShowCustomersList] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);

  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
  const isMountedRef = useRef(true);
  const {
    customers: existingCustomers,
    loading: loadingCustomers,
    searchCustomers,
    getAllCustomers,
    updateCustomer,
    getCustomerByMobileNo,
    currentCustomerNewForm,
    insertCustomer,
  } = useCustomerSearch();

  const {
    countries,
    states,
    cities,
    loading: { countries: countriesLoading, states: statesLoading, cities: citiesLoading },
  } = useLocationSearch();
  const [walkInSources, setWalkInSources] = useState<SelectListItem[]>([])


  useEffect(() => {
    if (show) {
      getAllCustomers(1, 50);

      // Only reset form if no current customer is selected
      if (!currentCustomer) {
        setNewCustomerForm(getInitialFormState(countries, states, cities, user));
      }
    }
  }, [show, getAllCustomers, currentCustomer, states, cities, user]);

  // Set India as default country when countries are loaded
  useEffect(() => {
    if (countries.length > 0 && !newCustomerForm.country && !currentCustomer && show) {
      const indiaCountry = countries.find(c =>
        c.text.toLowerCase().includes('india') ||
        c.text.toLowerCase() === 'india'
      );
      if (indiaCountry) {
        setNewCustomerForm(prev => ({ ...prev, country: indiaCountry }));
      }
    }
  }, [countries, newCustomerForm.country, currentCustomer, show]);

  // Load states when country changes
  useEffect(() => {
    if (newCustomerForm.country?.value && !currentCustomer) {
    }
  }, [newCustomerForm.country?.value, currentCustomer]);

  // Load cities when state changes
  useEffect(() => {
    if (newCustomerForm.state?.value && !currentCustomer) {
    }
  }, [newCustomerForm.state?.value, currentCustomer]);

  const getWalkins = async () => {
    try {
      const response = await getAllWalkInSourceForSelect()
      if (response.status == HttpStatus.Success) {
        setWalkInSources(response.data)
      }
    } catch (error) {
      // notistack.error(error)
      console.log(error)
    }
  }

  // Handle customer search
  useEffect(() => {
    getWalkins()
    if (customerSearchTerm.trim()) {
      searchCustomers(customerSearchTerm);
    } else if (show) {
      getAllCustomers(currentPage, itemsPerPage);
    }
  }, [customerSearchTerm, show, currentPage, itemsPerPage, searchCustomers, getAllCustomers]);

  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-based
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return "";
    }
  };



  // Populate form with customer data when currentCustomer changes
  useEffect(() => {
    const populateCustomerData = async () => {
      if (show && currentCustomer) {
        const foundCustomer: any = currentCustomerNewForm

        if (foundCustomer) {
          try {

            const timeoutId = setTimeout(() => {
              if (!isMountedRef.current) return;

              const customerCountry = countries.find((c) =>
                c.value === foundCustomer?.countryId || c.text === foundCustomer?.country
              ) || null;

              const customerState = states.find((s) =>
                s.value === foundCustomer?.stateId || s.text === foundCustomer?.state
              ) || null;

              const customerCity = cities.find((c) =>
                c.value === foundCustomer?.cityId || c.text === foundCustomer?.city
              ) || null;

              const formData = {
                prefix: "+91",
                mobileNo: foundCustomer?.mobileNo || "",
                firstName: foundCustomer?.firstName || "",
                lastName: foundCustomer?.lastName || "",
                email: foundCustomer?.email || "",
                address: foundCustomer?.address || "",
                country: customerCountry,
                state: customerState,
                city: customerCity,
                zipCode: foundCustomer?.zipCode || "",
                anniversary: formatDateForInput(foundCustomer?.anniversaryDate),
                birthday: formatDateForInput(foundCustomer?.birthdayDate),
                engagement: formatDateForInput(foundCustomer?.engagementDate),
                spouseName: foundCustomer?.partnerName || "",
                spouseContactNo: foundCustomer?.partnerContactNo || "",
                spouseBirthday: formatDateForInput(foundCustomer?.partnerBirthdayDate),
                gender: foundCustomer?.gender || "",
                altMobileNo: foundCustomer.altMobileNo || '',
                gstNo: foundCustomer?.gstNo || "",
                panCardNo: foundCustomer?.panCardNo || "",
                walkInSourceId: 1,
                customerAddressList: foundCustomer?.customerAddressList
              };

              // setNewCustomerForm(formData);
              setLastCheckedMobile(foundCustomer?.mobileNo || "");
            }, 500);
            timeoutRefs.current.push(timeoutId);
          } catch (error) {
            console.error('Error loading customer location data:', error);
            toast.error('Failed to load customer location data');
          }
        }
      }
    };

    populateCustomerData();
  }, [show, currentCustomer, existingCustomers, countries, states, cities]);


  const { getCustomerAvailableRewardPoints } = useCustomerRewardPoints();
  const isApplyRef = useRef(false);
  const handleEditCustomer = async (customer: CustomerData, billingAddressId?: number, shippingAddressId?: number) => {
    try {
      const response = await getCustomerAvailableRewardPoints(customer.id);
      const points =
        response?.availablePoints && !isNaN(Number(response.availablePoints))
          ? Number(response.availablePoints)
          : 0;

      const creditDetails = await getCustomerCreditAmountById(customer.id);

      const hasBillingAddress = customer?.customerAddressList?.some(
        (addr: any) => addr.id === billingAddressId
      ) ?? false;

      const hasShippingAddress = customer?.customerAddressList?.some(
        (addr: any) => addr.id === shippingAddressId
      ) ?? false;

      if (hasBillingAddress == false && hasShippingAddress == false) {
        setSelectedShippingId(null)
        setShippingId(null)
      }

      const customerForEditing = {
        id: customer.id,
        name: `${customer.firstName} ${customer.lastName}`,
        phone: customer.mobileNo,
        email: customer.email,
        isIgst: creditDetails?.data?.isIgst,
        creditAmount: creditDetails?.data?.creditAmount,
        ShippingAddressId: hasBillingAddress ? (billingAddressId ?? null) : null,
        BillingAddressId: hasShippingAddress ? (shippingAddressId ?? null) : null,
        rewardPoints: points ?? 0,
      };
      onCustomerSelect(customerForEditing);

      const customerCountry = countries.find((c) =>
        c.value === customer.countryId || c.text === customer.country
      ) || null;

      const customerState = states.find((s) =>
        s.value === customer.stateId || s.text === customer.state
      ) || null;

      const customerCity = cities.find((c) =>
        c.value === customer.cityId || c.text === customer.city
      ) || null;

      const formData = {
        prefix: "+91",
        mobileNo: customer.mobileNo || "",
        firstName: customer.firstName || "",
        lastName: customer.lastName || "",
        email: customer.email || "",
        address: customer.address || "",
        country: customerCountry,
        state: customerState,
        city: customerCity,
        zipCode: customer.zipCode || "",
        anniversary: formatDateForInput(customer.anniversaryDate),
        birthday: formatDateForInput(customer.birthdayDate),
        engagement: formatDateForInput(customer.engagementDate),
        spouseName: customer.partnerName || "",
        spouseContactNo: customer.partnerContactNo || "",
        spouseBirthday: formatDateForInput(customer.partnerBirthdayDate),
        gender: customer.gender || '',
        altMobileNo: customer.altMobileNo,
        gstNo: customer.gstNo || "",
        panCardNo: customer.panCardNo || "",
        walkInSourceId: customer.walkInSourceId || "",
        customerAddressList: customer?.customerAddressList
      };

      setNewCustomerForm(formData);
      const rewardDataResult = rewardDataResultRef.current;
      if (rewardDataResult?.distribution) {
        if (rewardDataResult?.totalUsedPoints < points) {
          if (isApplyRef.current) return;
          isApplyRef.current = true;

          actions.setRewardRedeemDetails(
            rewardDataResult.redeemDetails?.map((detail: any, idx: number) => ({
              id: idx,
              materialTypeId: detail.materialTypeId,
              materialTypeName: detail.materialTypeName,
              usedPoints: detail.usedPoints,
              amountConverted: detail.amountConverted,
              totalAmt: detail.totalAmt,
              rewardPercentage: detail.rewardPercentage,
              isClub: detail.isClub,
            })) || []
          );

          actions.setRewardPoints(
            rewardDataResult?.materialRedeemPoints
              ? Number(Object.values(rewardDataResult?.materialRedeemPoints).reduce((a: any, b: any) => a + b, 0))
              : 0
          );
          actions.applyRewardPoints(rewardDataResult?.distribution ?? {});
        }
      }
    } catch (error) {
      console.error("Error loading customer data for editing:", error);
      toast.error("Failed to load customer data for editing.");
    }
  };

  const checkMobileNumberExists = async (mobileNo: string) => {
    if (!mobileNo || mobileNo.length < 10 || mobileNo === lastCheckedMobile) {
      return;
    }

    if (currentCustomer?.phone === mobileNo) {
      return;
    }

    try {
      setFormSubmitting(true)
      setIsCheckingMobile(true);
      setLastCheckedMobile(mobileNo);

      const existingCustomer = await getCustomerByMobileNo(mobileNo);
      const creditDetails = await getCustomerCreditAmountById(existingCustomer.id);

      if (existingCustomer) {
        setMobileExistsInDB(true);
        toast.info(
          `Customer found: ${existingCustomer.firstName} ${existingCustomer.lastName} - Loading details...`,
          {
            position: "top-right",
            autoClose: 2000,
          }
        );

        await fillCustomerData(existingCustomer);

        const customerForSelection = {
          id: existingCustomer.id,
          name: `${existingCustomer.firstName} ${existingCustomer.lastName}`,
          phone: existingCustomer.mobileNo,
          email: existingCustomer.email,
          isIgst: creditDetails?.data?.isIgst,
          creditAmount: creditDetails?.data?.creditAmount
        };
        onCustomerSelect(customerForSelection);
      } else {
        setMobileExistsInDB(false);
      }
    } catch (error) {
      console.error('Error checking mobile number:', error);
      setMobileExistsInDB(false);
      setFormSubmitting(false)
    } finally {
      setIsCheckingMobile(false);
      setFormSubmitting(false)

    }
  };

  const fillCustomerData = async (existingCustomer: any) => {
    try {
      const creditDetails = await getCustomerCreditAmountById(existingCustomer.id);

      const timeoutId = setTimeout(() => {
        if (!isMountedRef.current) return;
        const customerCountry = countries.find((c) =>
          c.value === existingCustomer.countryId || c.text === existingCustomer.country
        ) || null;

        const customerState = states.find((s) =>
          s.value === existingCustomer.stateId || s.text === existingCustomer.state
        ) || null;

        const customerCity = cities.find((c) =>
          c.value === existingCustomer.cityId || c.text === existingCustomer.city
        ) || null;


        setNewCustomerForm({
          prefix: "+91",
          mobileNo: existingCustomer.mobileNo || "",
          firstName: existingCustomer.firstName || "",
          lastName: existingCustomer.lastName || "",
          email: existingCustomer.email || "",
          address: existingCustomer.address || "",
          country: customerCountry,
          state: customerState,
          city: customerCity,
          zipCode: existingCustomer.zipCode || "",
          anniversary: formatDateForInput(existingCustomer.anniversaryDate),
          birthday: formatDateForInput(existingCustomer.birthdayDate),
          engagement: formatDateForInput(existingCustomer.engagementDate),
          spouseName: existingCustomer.partnerName || "",
          spouseContactNo: existingCustomer.partnerContactNo || "",
          spouseBirthday: formatDateForInput(existingCustomer.partnerBirthdayDate),
          gender: existingCustomer.gender || '',
          altMobileNo: existingCustomer.altMobileNo || '',
          gstNo: existingCustomer.gstNo || "",
          panCardNo: existingCustomer.panCardNo || "",
          walkInSourceId: existingCustomer.walkInSourceId || "",
          customerAddressList: existingCustomer?.customerAddressList

        });
      }, 300);
      timeoutRefs.current.push(timeoutId);
    } catch (error) {
      console.error('Error filling customer data:', error);
    }
  };

  const updateNewCustomerForm = (
    field: keyof NewCustomerForm,
    value: string | boolean | any
  ) => {
    let processedValue = value;

    if (typeof value === 'string' && (field === 'firstName' || field === 'lastName' || field === 'email' || field === 'address')) {
      processedValue = value.trimStart();
    }

    if (typeof value === 'string' && (field === 'gstNo' || field === 'panCardNo')) {
      processedValue = value.toUpperCase();
    }
    if (field === 'walkInSourceId') {
      processedValue = value === "" ? "" : value; // Keep empty string or the selected value
    }
    setNewCustomerForm((prev) => ({ ...prev, [field]: processedValue }));

    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }

    if (field === 'mobileNo' && typeof value === 'string') {
      const cleanMobile = value.replace(/\D/g, '');

      if (cleanMobile !== lastCheckedMobile) {
        setLastCheckedMobile("");
        setMobileExistsInDB(false);
      }

      if (cleanMobile.length > 10) {
        const truncatedMobile = cleanMobile.slice(0, 10);
        setNewCustomerForm((prev) => ({ ...prev, [field]: truncatedMobile }));
        if (formErrors[field]) {
          setFormErrors((prev) => ({ ...prev, [field]: "" }));
        }
        checkMobileNumberExists(truncatedMobile);
        return;
      } else if (cleanMobile.length === 10) {
        checkMobileNumberExists(cleanMobile);
      }
    }

    if (field === 'country') {
      setNewCustomerForm((prev) => ({ ...prev, state: null, city: null }));
      setFormErrors((prev) => ({ ...prev, state: "", city: "" }));
    }

    if (field === 'state') {
      setNewCustomerForm((prev) => ({ ...prev, city: null }));
      setFormErrors((prev) => ({ ...prev, city: "" }));
    }
  };

  const handleMobileBlur = () => {
    const mobileNo = newCustomerForm.mobileNo.replace(/\D/g, '');
    if (mobileNo.length > 10) {
      const truncatedMobile = mobileNo.slice(0, 10);
      updateNewCustomerForm('mobileNo', truncatedMobile);
      checkMobileNumberExists(truncatedMobile);
    } else if (mobileNo.length === 10) {
      checkMobileNumberExists(mobileNo);
    }
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};
    const validateDate = (dateStr: string, fieldName: string) => {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        errors[fieldName] = `Please enter a valid ${fieldName} date`;
      } else if (fieldName !== "engagement" && date > new Date()) {
        errors[fieldName] = `${fieldName[0].toUpperCase() + fieldName.slice(1)} date cannot be in the future`;
      }
    };

    const mobileNo = newCustomerForm.mobileNo?.replace(/\D/g, '') || "";
    if (!mobileNo) {
      errors.mobileNo = "Mobile number is required";
    } else if (mobileNo.length !== 10) {
      errors.mobileNo = "Mobile number must be exactly 10 digits";
    } else if (/^(\d)\1{9}$/.test(mobileNo)) {
      errors.mobileNo = "Mobile number cannot have all digits the same";
    }

    const firstName = newCustomerForm.firstName?.trim() || "";
    if (!firstName) {
      errors.firstName = "First name is required";
    } else if (firstName.length < 2) {
      errors.firstName = "First name must be at least 2 characters";
    } else if (firstName.length > 50) {
      errors.firstName = "First name cannot exceed 50 characters";
    } else if (!/^[A-Za-z\s]+$/.test(firstName)) {
      errors.firstName = "First name must contain only letters";
    }

    const lastName = newCustomerForm.lastName?.trim() || "";
    if (!lastName) {
      errors.lastName = "Last name is required";
    } else if (lastName.length < 2) {
      errors.lastName = "Last name must be at least 2 characters";
    } else if (lastName.length > 50) {
      errors.lastName = "Last name cannot exceed 50 characters";
    } else if (!/^[A-Za-z\s]+$/.test(lastName)) {
      errors.lastName = "Last name must contain only letters";
    }

    const email = newCustomerForm.email?.trim().toLowerCase() || "";
    if (email) {
      if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
        errors.email = "Please enter a valid email address";
      } else if (email.length > 100) {
        errors.email = "Email cannot exceed 100 characters";
      }
    }

    const address = newCustomerForm.address?.trim() || "";
    if (address.length > 200) {
      errors.address = "Address cannot exceed 200 characters";
    }

    const zip = newCustomerForm.zipCode?.trim() || "";
    if (zip && !/^[1-9][0-9]{5}$/.test(zip)) {
      errors.zipCode = "ZIP code must be a valid 6-digit number";
    }

    const gstNo = newCustomerForm.gstNo?.trim() || "";
    if (gstNo && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gstNo)) {
      errors.gstNo = "Please enter a valid GST number";
    }

    const panCard = newCustomerForm.panCardNo?.trim() || "";
    if (panCard && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(panCard)) {
      errors.panCardNo = "Please enter a valid PAN card number";
    }

    const gender = newCustomerForm.gender;
    if (gender && !["Male", "Female", "Other"].includes(gender)) {
      errors.gender = "Please select a valid gender";
    }

    const altMobile = newCustomerForm.altMobileNo?.replace(/\D/g, '') || "";
    if (altMobile && !/^[6-9]\d{9}$/.test(altMobile)) {
      errors.altMobileNo = "Please enter a valid alternate mobile number";
    }

    const spouseContact = newCustomerForm.spouseContactNo?.replace(/\D/g, '') || "";
    if (spouseContact && !/^[6-9]\d{9}$/.test(spouseContact)) {
      errors.spouseContactNo = "Please enter a valid spouse contact number";
    }

    const spouseName = newCustomerForm.spouseName?.trim() || "";
    if (spouseName && spouseName.length > 50) {
      errors.spouseName = "Spouse name cannot exceed 50 characters";
    } else if (spouseName && !/^[A-Za-z]+(?:\s[A-Za-z]+)*$/.test(spouseName)) {
      errors.spouseName = "Spouse name must contain only letters";
    }

    if (newCustomerForm.anniversary) validateDate(newCustomerForm.anniversary, "anniversary");
    if (newCustomerForm.birthday) validateDate(newCustomerForm.birthday, "birthday");
    if (newCustomerForm.engagement) validateDate(newCustomerForm.engagement, "engagement");
    if (newCustomerForm.spouseBirthday) validateDate(newCustomerForm.spouseBirthday, "spouseBirthday");

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const formatCustomerDataForAPI = (formData: NewCustomerForm) => {
    const payload: any = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      mobileNo: formData.mobileNo,
      gender: formData.gender,
      email: formData.email,
      address: formData.address,
      countryId: formData.country?.value ? parseInt(formData.country.value) : 1,
      stateId: formData.state?.value ? parseInt(formData.state.value) : 1,
      cityId: formData.city?.value ? parseInt(formData.city.value) : 1,
      zipCode: formData.zipCode,
      walkInSourceId: formData.walkInSourceId ? Number(formData.walkInSourceId) : null,
      customerAddressList: formData.customerAddressList

    };

    if (formData.anniversary && formData.anniversary.trim() !== "") {
      payload.anniversaryDate = new Date(formData.anniversary).toISOString();
    }
    if (formData.birthday && formData.birthday.trim() !== "") {
      payload.birthdayDate = new Date(formData.birthday).toISOString();
    }
    if (formData.engagement && formData.engagement.trim() !== "") {
      payload.engagementDate = new Date(formData.engagement).toISOString();
    }
    if (formData.spouseBirthday && formData.spouseBirthday.trim() !== "") {
      payload.partnerBirthdayDate = new Date(formData.spouseBirthday).toISOString();
    }

    if (formData.spouseName && formData.spouseName.trim() !== "") {
      payload.partnerName = formData.spouseName;
    }
    if (formData.spouseContactNo && formData.spouseContactNo.trim() !== "") {
      payload.partnerContactNo = formData.spouseContactNo;
    }
    if (formData.altMobileNo && formData.altMobileNo.trim() !== "") {
      payload.altMobileNo = formData.altMobileNo;
    }
    if (formData.gstNo && formData.gstNo.trim() !== "") {
      payload.gstNo = formData.gstNo;
    }
    if (formData.panCardNo && formData.panCardNo.trim() !== "") {
      payload.panCardNo = formData.panCardNo;
    }

    return payload;
  };

  const [formSubmitting, setFormSubmitting] = useState(false);

  const handleNewCustomerSubmit = async (updatedForm?: NewCustomerForm) => {
    const formDataToUse = updatedForm || newCustomerForm;
    if (!validateForm()) return;

    setFormSubmitting(true);
    try {
      let result;

      if (currentCustomer?.id) {
        const updatePayload = formatCustomerDataForAPI(formDataToUse);
        const payload = {
          ...updatePayload,
          customerAddressList: formDataToUse.customerAddressList,
        };

        result = await updateCustomer(currentCustomer.id, payload);

        if (!result) {
          toast.error("Failed to update customer - no response from server");
          return;
        }

        toast.success(
          `Customer ${formDataToUse.firstName} ${formDataToUse.lastName} updated successfully!`
        );

        const resp = await getCustomerByMobileNo(formDataToUse.mobileNo);
        const creditDetails = await getCustomerCreditAmountById(currentCustomer.id);

        setNewCustomerForm(resp);
        onCustomerSelect({
          id: resp.id,
          name: `${resp.firstName} ${resp.lastName}`,
          phone: resp.mobileNo,
          email: resp.email,
          // @ts-expect-error bypass type check
          isIgst: creditDetails?.data?.isIgst ?? false,
          creditAmount: creditDetails?.data?.creditAmount ?? 0,
        });
      }

      else {
        const existingCustomer = await getCustomerByMobileNo(formDataToUse.mobileNo);

        if (existingCustomer) {
          const creditDetails = await getCustomerCreditAmountById(existingCustomer.id);

          toast.success(
            `Customer found: ${existingCustomer.firstName} ${existingCustomer.lastName} - Selected successfully!`
          );

          onCustomerSelect({
            id: existingCustomer.id,
            name: `${existingCustomer.firstName} ${existingCustomer.lastName}`,
            phone: existingCustomer.mobileNo,
            email: existingCustomer.email,
            // @ts-expect-error bypass type check
            isIgst: creditDetails?.data?.isIgst,
            creditAmount: creditDetails?.data?.creditAmount,
          });

          resetCustomerForm();
          return;
        }

        const insertPayload = formatCustomerDataForAPI(formDataToUse);
        const payload = {
          ...insertPayload,
          customerAddressList: formDataToUse.customerAddressList || [],
        };

        const response = await insertCustomer(payload);

        if (!response || response.status < 200 || response.status >= 300) {
          const errorMessage =
            response?.data?.message ||
            response?.data?.error ||
            response?.data?.title ||
            `Failed to create customer - Server returned status: ${response?.status}`;
          toast.error(errorMessage);
          return;
        }

        toast.success(
          `New customer ${formDataToUse.firstName} ${formDataToUse.lastName} added successfully!`
        );

        const resp = await getCustomerByMobileNo(formDataToUse.mobileNo);
        if (resp) {
          setNewCustomerForm(resp);

          const creditDetails = await getCustomerCreditAmountById(resp.id);

          onCustomerSelect({
            id: resp.id,
            name: `${resp.firstName} ${resp.lastName}`,
            phone: resp.mobileNo,
            email: resp.email,
            // @ts-expect-error bypass type check
            isIgst: creditDetails?.data?.isIgst,
            creditAmount: creditDetails?.data?.creditAmount,
          });
        }
      }
      // resetCustomerForm();

    } catch (error: any) {
      console.error("Error saving customer:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to save customer";
      toast.error(errorMessage, { position: "top-right", autoClose: 5000 });
    } finally {
      setFormSubmitting(false);
      setActiveTab("address");

    }
  };
  const resetCustomerForm = () => {
    setNewCustomerForm(getInitialFormState(countries));
    setFormErrors({});
    setLastCheckedMobile("");
    setIsCheckingMobile(false);
    setMobileExistsInDB(false);
    getAllCustomers(1, 50);
  };



  const handleRemove = () => {
    actions.clearRewardRedeemDetails();
    actions.removeRewardPoints()
    actions.setRewardPoints(0);
    onCustomerSelect(null as any);
    setSelectedShippingId(null);
    setShippingId(null);
    // onHide();
  };

  const handleClose = () => {
    setCustomerSearchTerm("");
    setFormErrors({});
    setLastCheckedMobile("");
    setIsCheckingMobile(false);
    setMobileExistsInDB(false);
    setCurrentPage(1);

    if (!currentCustomer) {
      setNewCustomerForm(getInitialFormState(countries));
    }

    onHide();
  };

  const filteredCustomers = existingCustomers.filter((customer) =>
    (customer.firstName?.toLowerCase() ?? "").includes(customerSearchTerm.toLowerCase()) ||
    (customer.lastName?.toLowerCase() ?? "").includes(customerSearchTerm.toLowerCase()) ||
    (customer.mobileNo ?? "").includes(customerSearchTerm) ||
    (customer.email?.toLowerCase() ?? "").includes(customerSearchTerm.toLowerCase()) ||
    String(customer.id ?? "").toLowerCase().includes(customerSearchTerm.toLowerCase())
  );

  const totalItems = filteredCustomers.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCustomers = filteredCustomers.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [customerSearchTerm]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      timeoutRefs.current.forEach(clearTimeout);
      timeoutRefs.current = [];
    };
  }, []);




  const renderFormField = (
    label: string,
    field: keyof NewCustomerForm,
    type: "text" | "email" | "date" | "radio" = "text",
    required: boolean = false,
    placeholder?: string,
    disabled?: boolean
  ) => {
    if (field === 'mobileNo') {
      const mobileNo = newCustomerForm[field] as string;
      const cleanMobile = mobileNo.replace(/\D/g, '');

      let borderColor = '';
      let textColor = '';
      let helpText = '';
      const isAllSameDigits = /^(\d)\1{9}$/.test(cleanMobile);

      if (cleanMobile.length === 10) {
        if (isAllSameDigits) {
          borderColor = 'border-danger';
          textColor = 'text-danger';
          helpText = 'Mobile number cannot have all same digits';
        } else if (currentCustomer?.phone === cleanMobile) {
          borderColor = 'border-primary';
          textColor = 'text-primary';
          helpText = ' ';
        } else if (isCheckingMobile) {
          borderColor = 'border-warning';
          textColor = 'text-warning';
          helpText = 'Checking mobile number...';
        } else if (lastCheckedMobile === cleanMobile) {
          if (mobileExistsInDB) {
            borderColor = '';
            textColor = 'text-danger';
            helpText = '';
          } else {
            borderColor = 'border-info';
            textColor = 'text-info';
          }
        } else {
          borderColor = 'border-info';
          textColor = 'text-info';
          helpText = 'mobile_phone: 10-digit mobile number entered';
        }
      } else if (cleanMobile.length > 0) {
        borderColor = 'border-secondary';
        textColor = 'text-muted';
        helpText = `Enter ${10 - cleanMobile.length} more digits`;
      }

      return (
        <Form.Group>
          <Form.Label style={{ marginBottom: 0 }}>
            {label} {required && <span className="text-danger">*</span>}
          </Form.Label>
          <InputGroup>
            <Form.Control
              size="sm"
              type="tel"
              autoComplete="off"
              value={mobileNo}
              autoFocus
              onChange={(e) => {
                let value = e.target.value.replace(/\D/g, '');
                if (value.length > 10) {
                  value = value.slice(0, 10);
                }
                updateNewCustomerForm(field, value);
                setFormErrors({});
              }}
              onBlur={handleMobileBlur}
              onKeyDown={(e: any) => {
                const allowedKeys = ["Backspace", "ArrowLeft", "ArrowRight", "Tab", "Delete", "Enter"];
                const isCtrl = e.ctrlKey || e.metaKey; // allow Mac ⌘
                // Allow Ctrl+C, Ctrl+V, Ctrl+X
                if (isCtrl && ["c", "v", "x"].includes(e.key.toLowerCase())) {
                  return;
                }
                if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                  e.preventDefault();
                }
              }}
              placeholder={placeholder || "Enter 10 digit mobile number"}
              maxLength={10}
              inputMode="numeric"
              pattern="[0-9]*"
              disabled={disabled}
            />
            {isCheckingMobile && (
              <InputGroup.Text>
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">Checking...</span>
                </div>
              </InputGroup.Text>
            )}
          </InputGroup>
          {formErrors[field] && (
            <div className="text-danger small mt-1">{formErrors[field]}</div>
          )}
          <Form.Text className={textColor}>
            {helpText}
          </Form.Text>
        </Form.Group>
      );
    }

    return (
      <Form.Group>
        <Form.Label style={{ marginBottom: 0 }}>
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
        {type === "radio" ? (
          <div className="mt-2">
            <Form.Check
              inline
              type="radio"
              label="Yes"
              name={field}
              checked={newCustomerForm[field] === true}
              onChange={() => updateNewCustomerForm(field, true)}
            />
            <Form.Check
              inline
              type="radio"
              label="No"
              name={field}
              checked={newCustomerForm[field] === false}
              onChange={() => updateNewCustomerForm(field, false)}
            />
          </div>
        ) : (
          <Form.Control
            size="sm"
            type={type}
            value={newCustomerForm[field] as string}
            // onChange={(e) => updateNewCustomerForm(field, e.target.value)}
            onChange={(e) => {
              let value = e.target.value;
              if (field === "firstName" || field == 'lastName' || field == 'spouseName') {
                // Allow only letters and spaces
                value = value.replace(/[^a-zA-Z\s]/g, "");
              }
              if (field === "altMobileNo" || field === "spouseContactNo") {
                value = value.replace(/\D/g, "");
                if (value.length > 10) {
                  value = value.slice(0, 10);
                }

                if (/^(\d)\1{9}$/.test(value)) {
                  value = "";
                  setFormErrors((prev) => ({
                    ...prev,
                    [field]: `${field === "altMobileNo" ? "Alt Mobile" : "Spouse Mobile"} number all digits cannot be the same`,
                  }));
                } else {
                  setFormErrors((prev) => {
                    const updated = { ...prev };
                    delete updated[field];
                    return updated;
                  });
                }
              }

              updateNewCustomerForm(field, value);
            }}
            placeholder={placeholder || `Enter ${label.toLowerCase()}`}
            isInvalid={!!formErrors[field]}
            disabled={disabled}
            autoComplete={"abcxyx-ajj"}
            style={
              formErrors[field]
                ? { borderColor: "#ced4da" }
                : undefined
            }
            onInput={(e: any) => {
              let value = e.target.value.replace(/\s+/g, ' ');
              if (value.startsWith(' ')) {
                value = value.trimStart();
              }
              e.target.value = value;
            }}
          />
        )}
        {formErrors[field] && (
          <Form.Control.Feedback type="invalid">
            {formErrors[field]}
          </Form.Control.Feedback>
        )}
      </Form.Group>
    );
  };

  // console.log(!!(newCustomerForm?.firstName && newCustomerForm?.lastName && newCustomerForm?.mobileNo))
  return (
    <>
      <style>
        {`
          .customer-list::-webkit-scrollbar {
            width: 6px;
          }
          .customer-list::-webkit-scrollbar-track {
            background: #f1f3f4;
            border-radius: 10px;
          }
          .customer-list::-webkit-scrollbar-thumb {
            background: #007bff;
            border-radius: 10px;
          }
          .customer-list::-webkit-scrollbar-thumb:hover {
            background: #0056b3;
          }
        `}
      </style>

      <Modal show={show}
        onHide={() => {
          if (newCustomerForm?.mobileNo && newCustomerForm?.firstName && newCustomerForm?.lastName) {
            if (selectedShippingId && selectedShipping) {
              handleClose();
              setActiveTab("customer");
            } else {
              notistack.warning("Please select the billing and shipping address");
            }
          } else {
            setActiveTab("customer");
            handleClose();
          }
        }}
        size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="flex-grow-1 pt-0">
              {currentCustomer ? (
                <div className="alert alert-success mb-0 p-2 d-flex align-items-center">
                  <i className="bx bx-check-circle me-2 text-success" style={{ fontSize: '20px' }}></i>
                  <div>
                    <div className="mb-1">
                      <span className=" fw-bold  text-success">Currently Selected Customer: </span>
                      <strong>{currentCustomer.name}</strong> - {currentCustomer.phone}
                    </div>
                  </div>
                </div>
              ) : (
                <h5 className="mb-0 ">
                  <i className="bx bx-user-plus me-2"></i>
                  Add New Customer
                </h5>
              )}
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto" }}>
          <Tabs
            id="customer-address-tabs"
            activeKey={activeTab}
            onSelect={(k: any) => setActiveTab(k || "customer")}
            className="mb-3"
          >

            <Tab eventKey="customer" title="Customer">
              <Row>
                <Col md={16}>
                  <Card className="h-100">
                    <Card.Body className="pt-2">
                      <Form noValidate>
                        <Row className="mb-2">
                          <Col md={4} className="mb-0">
                            {renderFormField(
                              "Prefix",
                              "prefix",
                              "text",
                              true,
                              "+91",
                              true
                            )}
                          </Col>
                          <Col md={4}>
                            {renderFormField(
                              "Mobile No.",
                              "mobileNo",
                              "text",
                              true,
                              "Enter 10-digit mobile number",
                              !!currentCustomer
                            )}
                          </Col>
                          <Col md={4}>
                            {renderFormField(
                              "First Name",
                              "firstName",
                              "text",
                              true,
                              "Enter first name"
                            )}
                          </Col>
                        </Row>

                        <Row className="mb-2">
                          <Col md={4}>
                            {renderFormField("Last Name", "lastName", "text", true)}
                          </Col>
                          <Col md={4}>
                            {renderFormField("Email", "email", "email", false)}
                          </Col>
                          <Col md={4}>
                            {renderFormField("Anniversary Date", "anniversary", "date")}
                          </Col>
                        </Row>


                        <Row className="mb-2">

                          <Col md={4}>
                            {renderFormField("Birthday Date", "birthday", "date", false)}
                          </Col>
                          <Col md={4}>
                            {renderFormField("Engagement Date", "engagement", "date")}
                          </Col>
                          <Col md={4}>
                            {renderFormField("Spouse Name", "spouseName", "text")}
                          </Col>
                        </Row>

                        <Row className="mb-2">


                          <Col md={4}>
                            {renderFormField(
                              "Spouse Mobile No",
                              "spouseContactNo",
                              "text"
                            )}
                          </Col>
                          <Col md={4}>
                            {renderFormField(
                              "Spouse Birthday Date",
                              "spouseBirthday",
                              "date"
                            )}
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label style={{ marginBottom: 0 }}>Gender</Form.Label>
                              <Form.Select
                                size="sm"
                                value={newCustomerForm.gender || ""}
                                onChange={(e) => updateNewCustomerForm("gender", e.target.value)}
                                isInvalid={!!formErrors.gender}
                              >
                                <option value="">Select Gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                              </Form.Select>
                              {formErrors.gender && (
                                <Form.Control.Feedback type="invalid">
                                  {formErrors.gender}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>

                        {/* Additional Information */}
                        <Row className="mb-2">


                          <Col md={4}>
                            {renderFormField(
                              "Alt Mobile No",
                              "altMobileNo",
                              "text"
                            )}
                          </Col>
                          <Col md={4}>
                            {renderFormField("GST No", "gstNo", "text")}
                          </Col>
                          <Col md={4}>
                            {renderFormField("PAN Card No", "panCardNo", "text")}
                          </Col>
                          <Col md={4} className="mt-2">
                            <Form.Group>
                              <Form.Label className='mb-0'>Walk In Source</Form.Label>
                              <Form.Select
                                className="form-select-sm"
                                value={newCustomerForm.walkInSourceId || ""}
                                onChange={(e) => updateNewCustomerForm("walkInSourceId", e.target.value)}
                                isInvalid={!!formErrors.walkInSourceId}
                              >
                                <option value="">-- WalkIn --</option>
                                {walkInSources?.map((c: any) => (
                                  <option key={c.value} value={c.value}>
                                    {c.text}
                                  </option>
                                ))}
                              </Form.Select>
                              {formErrors.walkInSourceId && (
                                <Form.Control.Feedback type="invalid">
                                  {formErrors.walkInSourceId}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                      </Form>
                    </Card.Body>
                  </Card>
                </Col>

                <div className="d-flex w-100 justify-content-end gap-2 mt-3">
                  <Button
                    style={{
                      backgroundColor: "#3498db",
                      border: "none",
                      color: "#fff",
                      padding: "6px 16px",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                    type="button"
                    onClick={async () => {
                      // Validate customer form first
                      const isValid = validateForm();
                      if (!isValid) {
                        toast.error("Please fill required customer details!");
                        return;
                      }

                      if (currentCustomer) {
                        // If editing, update customer and stay on this tab
                        await handleNewCustomerSubmit();
                        setActiveTab("address");

                      } else {
                        await handleNewCustomerSubmit();
                        setActiveTab("address");
                      }
                    }}
                    disabled={formSubmitting}
                  >
                    {formSubmitting ? (
                      <>
                        <CircularProgress size={16} className="me-2" />
                        {currentCustomer ? "Updating..." : "Validating..."}
                      </>
                    ) : (
                      currentCustomer ? "Update & Next" : "Save & Next"
                    )}
                  </Button>

                  <Button
                    style={{
                      backgroundColor: "#e74c3c", // red for remove
                      border: "none",
                      color: "#fff",
                    }}
                    onClick={handleRemove}
                  >
                    <i className="bx bx-trash me-1"></i>
                    Remove
                  </Button>
                </div>
              </Row>
            </Tab>

            {/* Address Tab */}
            <Tab eventKey="address" title="Address" disabled={!(newCustomerForm?.firstName && newCustomerForm?.lastName && newCustomerForm?.mobileNo)}>
              <Row>
                <Col md={12}>
                  <Card className="h-100">
                    <Card.Body className="pt-2">
                      <ShippingAddressModal
                        newCustomerForm={newCustomerForm}
                        setNewCustomerForm={setNewCustomerForm}
                        selectedShippingId={selectedShippingId}
                        selectedShipping={selectedShipping}
                        setSelectedShippingId={setSelectedShippingId}
                        setShippingId={setShippingId}
                        customerHasRequiredFields={!!(newCustomerForm?.firstName && newCustomerForm?.lastName && newCustomerForm?.mobileNo)}
                        currentCustomer={currentCustomer}
                        onSubmitCustomer={handleNewCustomerSubmit}
                        isSubmitting={formSubmitting}
                        closeModal={onHide}
                        onSelect={setActiveTab}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>

          </Tabs>


        </Modal.Body>
      </Modal >
    </>
  );
};
